import { networkStats } from '@/lib/network-stats';

/**
 * Centralized debug logger — toggle categories on/off for focused debugging.
 * Set `enabled` to false or remove individual flags to silence logs.
 */

const DEBUG = {
  enabled: true,
  rtdbEvents: true,       // RTDB event handler (msg-add, msg-status, conv-update)
  convStore: true,        // Conversation store mutations (prepend, update, fetch)
  crossTab: true,         // BroadcastChannel cross-tab sync
  firestoreReads: true,   // Firestore document read tracking (dev-mode quota monitor)
  notes: true,            // Inbox notes: fetch decision, docs read, parse results, filtering
};

function isEnabled(category: keyof Omit<typeof DEBUG, 'enabled'>): boolean {
  return DEBUG.enabled && DEBUG[category];
}

/** Accumulated Firestore read counts by collection path */
const _readsByPath: Record<string, number> = {};
let _firestoreReadCount = 0;

export const dbg = {
  // ── RTDB Events ──
  rtdbEvent(type: string, rawId: string, convId: string, extra?: Record<string, unknown>) {
    networkStats.rtdbEvent();
    if (!isEnabled('rtdbEvents')) return;
    console.log(`[RTDB] ${type} | rawId: ${rawId} | convId: ${convId}`, JSON.parse(JSON.stringify(extra || {})));
  },
  rtdbMsgAdd(convId: string, messageSid: string, alreadyExists: boolean, foundInStore: boolean) {
    if (!isEnabled('rtdbEvents')) return;
    console.log(`[RTDB][msg-add] convId: ${convId} | sid: ${messageSid} | exists: ${alreadyExists} | inStore: ${foundInStore}`);
  },
  rtdbConvUpdate(convId: string, rawFields: Record<string, unknown>, mappedFields: Record<string, unknown>) {
    if (!isEnabled('rtdbEvents')) return;
    console.log(`[RTDB][conv-update] convId: ${convId} | raw:`, rawFields, '| mapped:', mappedFields);
  },
  rtdbMsgStatus(convId: string, messageSid: string, status: string, messageExists: boolean) {
    if (!isEnabled('rtdbEvents')) return;
    console.log(`[RTDB][msg-status] convId: ${convId} | sid: ${messageSid} | status: ${status} | exists: ${messageExists}`);
  },

  // ── Conversation Store ──
  convPrepend(convId: string, found: boolean, normalizedId: string, storeIds: string[]) {
    if (!isEnabled('convStore')) return;
    if (found) {
      console.log(`[ConvStore] prepend — found convId: ${convId}`);
    } else {
      console.warn(`[ConvStore] prepend — NOT FOUND → full reload | convId: ${convId} | normalized: ${normalizedId} | storeIds:`, storeIds.slice(0, 8));
    }
  },
  convUpdate(convId: string, fields: string[]) {
    if (!isEnabled('convStore')) return;
    console.log(`[ConvStore] update convId: ${convId} | fields:`, fields);
  },
  convFetchTriggered(reason: string) {
    if (!isEnabled('convStore')) return;
    console.log(`[ConvStore] fetchConversations triggered — reason: ${reason}`);
  },

  // ── Cross-Tab ──
  crossTabSend(type: string, data: unknown) {
    if (!isEnabled('crossTab')) return;
    console.log(`[CrossTab] send: ${type}`, data);
  },
  crossTabReceive(type: string, data: unknown) {
    if (!isEnabled('crossTab')) return;
    console.log(`[CrossTab] receive: ${type}`, data);
  },

  // ── Inbox Notes ──
  /** Whether the store performed its own notes read, and why not if it didn't */
  notesFetchDecision(convId: string, tpaId: string, skipped: boolean) {
    if (!isEnabled('notes')) return;
    console.log(
      `[Notes] ${skipped ? 'SKIPPED store read' : 'store read'} | tpa: ${tpaId} | conv: ${convId}` +
      (skipped ? ' — module reports getMessages() already returns notes' : '')
    );
  },
  /** Result of the messages-subcollection read that notes are parsed from */
  notesDocsRead(path: string | null, docCount: number) {
    if (!isEnabled('notes')) return;
    if (!path) {
      console.warn('[Notes] no inbox path — uid / extensionName / appCodeTPA not resolved yet');
      return;
    }
    console.log(`[Notes] read ${docCount} doc(s) from ${path}`);
  },
  /** Per-doc classification: what parsed as a note/system doc and what was skipped */
  notesParsed(parsed: number, skipped: Array<Record<string, unknown>>) {
    if (!isEnabled('notes')) return;
    console.log(`[Notes] parsed ${parsed} note/system doc(s)`);
    if (skipped.length > 0) {
      console.log(`[Notes] ${skipped.length} doc(s) not recognised as notes:`, skipped.slice(0, 10));
    }
  },
  /** How many notes survived the TPA time-window filter and reached the thread */
  notesMerged(fetched: number, kept: number, oldestTpaTime: number) {
    if (!isEnabled('notes')) return;
    const dropped = fetched - kept;
    const cutoff = oldestTpaTime > 0 ? new Date(oldestTpaTime).toISOString() : 'none';
    console.log(`[Notes] merged ${kept}/${fetched} into thread | older-than-window dropped: ${dropped} | cutoff: ${cutoff}`);
  },

  // ── Firestore Reads ──
  /** Log a Firestore read operation and accumulate counts */
  firestoreRead(path: string, docCount: number) {
    _firestoreReadCount += docCount;
    // Normalize path to collection level (strip doc IDs for grouping)
    const pathParts = path.split('/');
    // Keep up to the collection name (odd-index segments are doc IDs)
    const collectionKey = pathParts.length > 4
      ? `${pathParts.slice(0, 3).join('/')}/…/${pathParts[pathParts.length - 1]}`
      : path;
    _readsByPath[collectionKey] = (_readsByPath[collectionKey] || 0) + docCount;

    if (!isEnabled('firestoreReads')) return;
    console.log(
      `[Firestore] read ${docCount} doc${docCount !== 1 ? 's' : ''} from ${path} (session total: ${_firestoreReadCount})`
    );
  },

  /** Print accumulated Firestore read summary — call from browser console */
  firestoreReadSummary() {
    console.group(`[Firestore] Read Summary — ${_firestoreReadCount} total docs`);
    const sorted = Object.entries(_readsByPath).sort((a, b) => b[1] - a[1]);
    for (const [path, count] of sorted) {
      console.log(`  ${count.toLocaleString()} reads — ${path}`);
    }
    console.groupEnd();
  },

  /** Reset Firestore read counters */
  firestoreReadReset() {
    _firestoreReadCount = 0;
    for (const key of Object.keys(_readsByPath)) {
      delete _readsByPath[key];
    }
    console.log('[Firestore] Read counters reset');
  },
};

// Expose read summary on window for easy console access in dev mode
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__dbgFirestoreReads = () => dbg.firestoreReadSummary();
  (window as unknown as Record<string, unknown>).__dbgFirestoreReset = () => dbg.firestoreReadReset();
}
